import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Network, Wifi, Tv, Phone, ShoppingCart, ArrowRight, 
  CheckCircle, Zap, Shield, HeadphonesIcon
} from 'lucide-react';

const RequestService = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 'internet-leased-line',
      icon: <Network className="h-12 w-12" />,
      name: 'Internet Leased Line',
      tagline: 'Dedicated Symmetric Bandwidth',
      description: 'Premium connectivity solution with guaranteed speeds and SLA-backed uptime. Perfect for businesses requiring consistent, high-performance internet.',
      features: [
        'Dedicated bandwidth',
        'Symmetric upload/download',
        'SLA guarantee',
        '99.9% uptime',
        'Static IP addresses',
        'Priority support'
      ],
      image: 'https://images.unsplash.com/photo-1551703599-2a53f65da7e7?w=800&h=500&fit=crop',
      gradient: 'from-blue-600 to-cyan-600',
      speeds: ['5 Mbps', '10 Mbps', '50 Mbps', '100 Mbps', '500 Mbps', '1 Gbps']
    },
    {
      id: 'broadband-services',
      icon: <Wifi className="h-12 w-12" />,
      name: 'Broadband Services',
      tagline: 'High-Speed Internet for Everyone',
      description: 'Lightning-fast fiber optic broadband for homes and businesses. Unlimited data with no FUP limits. Perfect for streaming, gaming, and work from home.',
      features: [
        'Up to 1 Gbps speed',
        'Unlimited data',
        'No FUP limits',
        'Free installation',
        'Free router',
        '24/7 support'
      ],
      image: 'https://images.unsplash.com/photo-1604869515882-4d10fa4b0492?w=800&h=500&fit=crop',
      gradient: 'from-red-600 to-orange-600',
      speeds: ['5 Mbps', '50 Mbps', '100 Mbps', '200 Mbps', '500 Mbps', '1 Gbps']
    },
    {
      id: 'ott-services',
      icon: <Tv className="h-12 w-12" />,
      name: 'OTT Services',
      tagline: 'Seamless Streaming Experience',
      description: 'Optimized connectivity for Netflix, Prime Video, Disney+, and more. Buffer-free 4K streaming with minimal latency for live sports and events.',
      features: [
        '4K streaming support',
        'Zero buffering',
        'Multi-device support',
        'Low latency',
        'Optimized routing',
        'Parental controls'
      ],
      image: 'https://images.pexels.com/photos/1148820/pexels-photo-1148820.jpeg?w=800&h=500&fit=crop',
      gradient: 'from-purple-600 to-pink-600',
      speeds: ['5 Mbps', '50 Mbps', '100 Mbps', '200 Mbps']
    },
    {
      id: 'voip-services',
      icon: <Phone className="h-12 w-12" />,
      name: 'VoIP Services',
      tagline: 'Enterprise Voice Communication',
      description: 'Advanced Voice over IP solutions with HD voice quality, call recording, and significant cost savings compared to traditional phone systems.',
      features: [
        'HD voice quality',
        'Call recording',
        'Virtual numbers',
        'Cost-effective',
        'CRM integration',
        'Conference calling'
      ],
      image: 'https://images.pexels.com/photos/1181316/pexels-photo-1181316.jpeg?w=800&h=500&fit=crop',
      gradient: 'from-green-600 to-teal-600',
      speeds: ['Basic Plan', 'Business Plan', 'Enterprise Plan']
    }
  ];

  const handlePlaceOrder = (serviceName) => {
    navigate('/place-order-form', { state: { selectedService: serviceName } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 via-orange-500 to-yellow-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 backdrop-blur-md p-6 rounded-full border-2 border-white/40">
              <ShoppingCart className="h-16 w-16" />
            </div>
          </div>
          <h1 className="text-6xl font-bold mb-4 drop-shadow-lg">Request Service</h1>
          <p className="text-2xl text-white/90 max-w-3xl mx-auto mb-8">
            Choose from our range of high-speed internet and communication services
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-lg">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <CheckCircle className="h-5 w-5" />
              <span>Quick Installation</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Zap className="h-5 w-5" />
              <span>24/7 Support</span>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <Shield className="h-5 w-5" />
              <span>Reliable Service</span>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
          <p className="text-xl text-gray-600">
            Select a service and place your order in minutes
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <Card
              key={service.id}
              className="group hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-4 border-gray-200 overflow-hidden"
            >
              {/* Service Image */}
              <div className="relative h-64 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-60`}></div>
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-full">
                  <div className={`text-transparent bg-clip-text bg-gradient-to-r ${service.gradient}`}>
                    {service.icon}
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="text-3xl font-bold text-white drop-shadow-lg mb-1">
                    {service.name}
                  </h3>
                  <p className="text-lg text-white/90 drop-shadow">
                    {service.tagline}
                  </p>
                </div>
              </div>

              <CardContent className="p-6">
                {/* Description */}
                <p className="text-gray-700 mb-4 leading-relaxed">
                  {service.description}
                </p>

                {/* Features */}
                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    Key Features:
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-600"></div>
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Speed Options */}
                <div className="mb-6">
                  <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-orange-600" />
                    Available Plans:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {service.speeds.map((speed, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-sm font-semibold bg-gradient-to-r ${service.gradient} text-white shadow-md`}
                      >
                        {speed}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    onClick={() => handlePlaceOrder(service.name)}
                    className={`flex-1 h-14 text-lg font-bold bg-gradient-to-r ${service.gradient} hover:shadow-xl transition-all`}
                  >
                    <ShoppingCart className="h-5 w-5 mr-2" />
                    Place Order
                  </Button>
                  <Button
                    onClick={() => navigate(`/service/${service.id}`)}
                    variant="outline"
                    className="h-14 px-6 border-2"
                  >
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Quick Buy - Internet Plans with Pricing */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-red-500 to-pink-500 rounded-full text-white font-bold text-sm">
              Quick Buy - Instant Checkout
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Choose Your Internet Plan</h2>
            <p className="text-xl text-gray-400">Select a plan and complete your purchase in minutes</p>
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {/* Basic Plan */}
            <Card className="relative border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all group overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                  <Wifi className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">Basic</h3>
                <p className="text-gray-400 text-sm mb-4">50 Mbps Speed</p>
                <div className="mb-4">
                  <span className="text-4xl font-black text-white">₹399</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Unlimited Data
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Free Router
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    24/7 Support
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/checkout?plan=basic-50')}
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
                >
                  Buy Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Speed Plan - Popular */}
            <Card className="relative border-2 border-green-500 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all group overflow-hidden transform scale-105">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-bold px-4 py-1 rounded-full">
                MOST POPULAR
              </div>
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500"></div>
              <CardContent className="p-6 pt-8">
                <div className="w-14 h-14 mb-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Zap className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">Speed</h3>
                <p className="text-gray-400 text-sm mb-4">100 Mbps Speed</p>
                <div className="mb-4">
                  <span className="text-4xl font-black text-white">₹599</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Unlimited Data
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Free Router
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Static IP
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/checkout?plan=speed-100')}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                >
                  Buy Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Ultra Plan */}
            <Card className="relative border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all group overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500"></div>
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                  <Network className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">Ultra</h3>
                <p className="text-gray-400 text-sm mb-4">200 Mbps Speed</p>
                <div className="mb-4">
                  <span className="text-4xl font-black text-white">₹799</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Unlimited Data
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Priority Support
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Static IP
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/checkout?plan=ultra-200')}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                >
                  Buy Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            {/* Gigabit Plan */}
            <Card className="relative border-0 bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all group overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
              <CardContent className="p-6">
                <div className="w-14 h-14 mb-4 bg-gradient-to-br from-red-500 to-orange-500 rounded-xl flex items-center justify-center">
                  <Tv className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-1">Gigabit</h3>
                <p className="text-gray-400 text-sm mb-4">1 Gbps Speed</p>
                <div className="mb-4">
                  <span className="text-4xl font-black text-white">₹1999</span>
                  <span className="text-gray-400">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Premium Router
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Dedicated Manager
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-300">
                    <CheckCircle className="h-4 w-4 text-green-400" />
                    Static IP
                  </li>
                </ul>
                <Button
                  onClick={() => navigate('/checkout?plan=gigabit-1000')}
                  className="w-full bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600"
                >
                  Buy Now
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12">Why Choose Netix?</h2>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Zap className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">Fast Installation</h3>
              <p className="text-white/80">Quick setup within 48-72 hours</p>
            </div>
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <HeadphonesIcon className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">24/7 Support</h3>
              <p className="text-white/80">Round-the-clock customer assistance</p>
            </div>
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">Reliable Network</h3>
              <p className="text-white/80">99.9% uptime guarantee</p>
            </div>
            <div className="text-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="h-10 w-10" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Hidden Costs</h3>
              <p className="text-white/80">Transparent pricing</p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Need Help Choosing?</h2>
          <p className="text-xl text-gray-600 mb-8">
            Our team is here to help you select the perfect plan for your needs
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 justify-center">
            <Button
              onClick={() => window.location.href = 'tel:+919319919988'}
              data-testid="request-service-call-mobile-button"
              size="lg"
              className="bg-gradient-to-r from-red-600 to-orange-600 text-lg px-8 py-6"
            >
              <Phone className="h-5 w-5 mr-2" />
              Call Us: +91 93199 19988
            </Button>
            <Button
              onClick={() => window.location.href = 'tel:06276459015'}
              data-testid="request-service-call-office-button"
              size="lg"
              variant="outline"
              className="border-2 border-red-600 text-red-700 text-lg px-8 py-6"
            >
              <Phone className="h-5 w-5 mr-2" />
              Office: 06276 - 459015
            </Button>
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              variant="outline"
              className="border-2 text-lg px-8 py-6"
            >
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestService;
